import type { Point, WorkerRequest, WorkerResponse } from './types';
import { startCamera, grabFrame } from './camera';
import { Metrics } from './metrics';
import { drawCornersOverlay, fitCanvasToVideo, paintResult } from './ui';

const $video = document.getElementById('video') as HTMLVideoElement;
const $overlay = document.getElementById('overlay') as HTMLCanvasElement;
const $result = document.getElementById('result') as HTMLCanvasElement;
const $hud = document.getElementById('hud') as HTMLDivElement;
const $btnStart = document.getElementById('btn-start') as HTMLButtonElement;
const $btnShutter = document.getElementById('btn-shutter') as HTMLButtonElement;
const $btnBack = document.getElementById('btn-back') as HTMLButtonElement;

const metrics = new Metrics();
let worker: Worker | null = null;
let workerReady = false;
let workerBusy = false;
let lastCorners: Point[] | null = null;
let reqSeq = 0;
let rafId = 0;
let mode: 'live' | 'result' = 'live';

function setHud(text: string) {
  $hud.textContent = text;
}

function updateHud() {
  const m = metrics.snapshot();
  setHud(
    [
      `load ${m.loadMs == null ? '—' : `${m.loadMs.toFixed(0)}ms`}  fps ${m.fps.toFixed(1)}`,
      `detect ${m.detectMs.toFixed(0)}ms  warp ${m.warpMs == null ? '—' : `${m.warpMs.toFixed(0)}ms`}`,
      lastCorners ? '4-corner: OK' : '4-corner: searching…',
    ].join('\n'),
  );
}

async function preloadOpenCv(): Promise<string> {
  const resp = await fetch('/opencv/opencv.js');
  if (!resp.ok) throw new Error(`fetch opencv.js: HTTP ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

async function ensureWorker(): Promise<Worker> {
  if (worker) return worker;
  setHud('downloading OpenCV…');
  const cvUrl = await preloadOpenCv();
  worker = new Worker(new URL('./worker/cv.worker.ts', import.meta.url));
  worker.addEventListener('message', (e: MessageEvent<WorkerResponse>) => {
    const msg = e.data;
    switch (msg.type) {
      case 'ready':
        workerReady = true;
        metrics.recordLoad(msg.loadMs);
        $btnShutter.disabled = false;
        break;
      case 'detected':
        workerBusy = false;
        metrics.recordDetect(msg.latencyMs);
        lastCorners = msg.corners;
        break;
      case 'warped':
        metrics.recordWarp(msg.latencyMs);
        paintResult($result, msg.result);
        $result.style.display = 'block';
        mode = 'result';
        $btnShutter.disabled = true;
        $btnBack.disabled = false;
        break;
      case 'error':
        console.error('[worker]', msg.error);
        setHud(`ERROR: ${msg.error}`);
        workerBusy = false;
        break;
    }
  });
  worker.postMessage({ type: 'init', cvUrl } satisfies WorkerRequest);
  return worker;
}

async function loop() {
  if (mode !== 'live') {
    rafId = requestAnimationFrame(loop);
    return;
  }
  if (workerReady && !workerBusy) {
    const bitmap = await grabFrame($video, 720);
    if (bitmap) {
      workerBusy = true;
      const reqId = ++reqSeq;
      worker!.postMessage({ type: 'detect', bitmap, reqId } satisfies WorkerRequest, [bitmap]);
    }
  }
  drawCornersOverlay($overlay, $video, lastCorners);
  metrics.tickFrame();
  updateHud();
  rafId = requestAnimationFrame(loop);
}

$btnStart.addEventListener('click', async () => {
  try {
    $btnStart.disabled = true;
    setHud('starting camera…');
    await startCamera($video);
    fitCanvasToVideo($overlay, $video);
    fitCanvasToVideo($result, $video);
    rafId = requestAnimationFrame(loop);
    await ensureWorker();
    setHud('loading OpenCV…');
  } catch (err) {
    console.error(err);
    setHud(`ERROR: ${(err as Error).message ?? String(err)}`);
    $btnStart.disabled = false;
  }
});

$btnShutter.addEventListener('click', async () => {
  if (!workerReady || !lastCorners) return;
  const bitmap = await grabFrame($video, 0);
  if (!bitmap) return;
  const reqId = ++reqSeq;
  worker!.postMessage(
    { type: 'warp', bitmap, corners: lastCorners, outputLongSide: 1024, reqId } satisfies WorkerRequest,
    [bitmap],
  );
});

$btnBack.addEventListener('click', () => {
  $result.style.display = 'none';
  mode = 'live';
  $btnShutter.disabled = false;
  $btnBack.disabled = true;
});

window.addEventListener('beforeunload', () => {
  cancelAnimationFrame(rafId);
  worker?.terminate();
});

setHud('press Start  [build: fetch-v2]');
